/**
 * Step 1: Basic Information
 *
 * Advertiser, campaign name, pricing model, pricing rate, daily/total budget
 * and validity dates.
 */

import React, { useMemo } from "react";
import { Form, Row, Col, InputGroup } from "react-bootstrap";
import type { AdCampaignFormData, AdCampaignValidationErrors, AdCampaignPricingModelUI } from "../../../../../api/ads/types";
import { useAdvertisers } from "../../../../Ads/hooks/useAdvertisers";

import "../../../../Promos/Campaigns/CreateCampaign/components/StepperButtons.css";
import "../../../../Promos/Campaigns/CreateCampaign/components/StepperFormStyles.css";
import "../../../../../Common/FormStyles.css";

import "./StepBasicInfo.css";

type StepBasicInfoProps = {
  formData: AdCampaignFormData;
  validationErrors: AdCampaignValidationErrors;
  onChange: (updates: Partial<AdCampaignFormData>) => void;
  onNext: () => void;
  onCancel: () => void;
  onSaveDraft?: () => void;
  onEdit?: () => void;
  readOnly?: boolean;
  mode?: "create" | "edit" | "view";
};

const getTodayLocalISODate = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const pricingRateLabel: Record<string, string> = {
  CPC: "CPC Rate (per click)",
  CPM: "CPM Rate (per 1,000 impressions)",
  FLAT: "Flat Monthly Rate",
};

const StepBasicInfo: React.FC<StepBasicInfoProps> = ({ formData, validationErrors, onChange, onNext, onCancel, onSaveDraft, onEdit, readOnly = false, mode = "create" }) => {
  const today = useMemo(() => getTodayLocalISODate(), []);

  const { values: advValues, loading: advLoading } = useAdvertisers({ pageSize: 200, status: "ALL" });

  const pricingModelOptions: AdCampaignPricingModelUI[] = ["FLAT", "CPM", "CPC"];

  const pricingRateValue = typeof formData.pricingRate === "number" ? formData.pricingRate : "";
  const dailyBudgetValue = typeof formData.dailyBudget === "number" ? formData.dailyBudget : "";
  const totalBudgetValue = typeof formData.totalBudget === "number" ? formData.totalBudget : "";

  const minEnd = formData.startDate || today;

  const rateLabel = pricingRateLabel[formData.pricingModel as string] ?? "Rate";

  const isNextDisabled = useMemo(() => {
    if (!formData.advertiserId) return true;
    if (!formData.campaignName?.trim()) return true;
    if (!formData.pricingModel) return true;
    if (typeof formData.pricingRate !== "number" || formData.pricingRate <= 0) return true;
    if (typeof formData.dailyBudget !== "number" || formData.dailyBudget <= 0) return true;
    if (typeof formData.totalBudget !== "number" || formData.totalBudget <= 0) return true;
    if (!formData.startDate || !formData.endDate) return true;
    if (formData.startDate < today) return true;
    if (formData.endDate < today) return true;
    if (formData.endDate < formData.startDate) return true;
    return false;
  }, [
    formData.advertiserId,
    formData.campaignName,
    formData.pricingModel,
    formData.pricingRate,
    formData.dailyBudget,
    formData.totalBudget,
    formData.startDate,
    formData.endDate,
    today,
  ]);

  return (
    <form autoComplete="off" noValidate>
      <div className="mb-4">
        <h5 className="mb-2">Basic Information</h5>
      </div>

      <Row>
        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Advertiser</Form.Label>
            <Form.Select
              className="stepper-form-control"
              value={formData.advertiserId}
              onChange={(e) => {
                const id = e.target.value;
                const adv = advValues.advertisers.find((a) => a.id === id);
                onChange({ advertiserId: id, advertiserName: adv?.name || "" });
              }}
              isInvalid={!!validationErrors.advertiserId}
              disabled={readOnly || advLoading.listLoading}
            >
              <option value="">{advLoading.listLoading ? "Loading..." : "Select Advertiser"}</option>
              {advValues.advertisers.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.advertiserId}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Campaign name</Form.Label>
            <Form.Control
              type="text"
              className="stepper-form-control"
              placeholder="Summer Blast - 20% OFF"
              value={formData.campaignName}
              onChange={(e) => onChange({ campaignName: e.target.value })}
              isInvalid={!!validationErrors.campaignName}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={readOnly}
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.campaignName}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Pricing Model</Form.Label>
            <Form.Select
              className="stepper-form-control"
              value={formData.pricingModel}
              onChange={(e) => onChange({ pricingModel: e.target.value as AdCampaignPricingModelUI, pricingRate: "" })}
              isInvalid={!!validationErrors.pricingModel}
              disabled={readOnly}
            >
              <option value="">Select Pricing Model</option>
              {pricingModelOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.pricingModel}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">{rateLabel}</Form.Label>
            <InputGroup className="ads-budget-inputgroup">
              <InputGroup.Text className="ads-budget-inputgroup__prefix">₹</InputGroup.Text>
              <Form.Control
                type="number"
                className="stepper-form-control ads-budget-inputgroup__input"
                placeholder="0.00"
                value={pricingRateValue}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({ pricingRate: v === "" ? "" : Number(v) });
                }}
                isInvalid={!!validationErrors.pricingRate}
                min={0}
                step="0.01"
                disabled={readOnly}
              />
              <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                {validationErrors.pricingRate}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Daily Budget</Form.Label>
            <InputGroup className="ads-budget-inputgroup">
              <InputGroup.Text className="ads-budget-inputgroup__prefix">₹</InputGroup.Text>
              <Form.Control
                type="number"
                className="stepper-form-control ads-budget-inputgroup__input"
                placeholder="500"
                value={dailyBudgetValue}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({ dailyBudget: v === "" ? "" : Number(v) });
                }}
                isInvalid={!!validationErrors.dailyBudget}
                min={0}
                disabled={readOnly}
              />
              <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                {validationErrors.dailyBudget}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>

        <Col lg={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Total Budget</Form.Label>
            <InputGroup className="ads-budget-inputgroup">
              <InputGroup.Text className="ads-budget-inputgroup__prefix">₹</InputGroup.Text>
              <Form.Control
                type="number"
                className="stepper-form-control ads-budget-inputgroup__input"
                placeholder="45,000"
                value={totalBudgetValue}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({ totalBudget: v === "" ? "" : Number(v) });
                }}
                isInvalid={!!validationErrors.totalBudget}
                min={0}
                disabled={readOnly}
              />
              <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
                {validationErrors.totalBudget}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">Start Date</Form.Label>
            <Form.Control
              type="date"
              className="stepper-form-control"
              min={today}
              value={formData.startDate}
              onChange={(e) => onChange({ startDate: e.target.value })}
              isInvalid={!!validationErrors.startDate}
              disabled={readOnly}
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.startDate}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6} className="mb-3">
          <Form.Group className="stepper-form-group">
            <Form.Label className="stepper-form-label">End Date</Form.Label>
            <Form.Control
              type="date"
              className="stepper-form-control"
              min={minEnd}
              value={formData.endDate}
              onChange={(e) => onChange({ endDate: e.target.value })}
              isInvalid={!!validationErrors.endDate}
              disabled={readOnly}
            />
            <Form.Control.Feedback type="invalid" className="stepper-invalid-feedback">
              {validationErrors.endDate}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="stepper-buttons-container">
        <div className="stepper-buttons-left">
          <button type="button" className="stepper-back-button" onClick={onCancel}>
            <i className="ph-arrow-left" />
            Back
          </button>
        </div>
        <div className="stepper-buttons-right" style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
          {mode === "create" && onSaveDraft && (
            <button type="button" className="stepper-back-button" onClick={onSaveDraft} disabled={isNextDisabled}>
              Save as Draft
            </button>
          )}
          {mode === "edit" ? (
            <button type="button" className="stepper-next-button" onClick={onEdit} disabled={isNextDisabled}>
              Edit
            </button>
          ) : (
            <button type="button" className="stepper-next-button" onClick={onNext} disabled={mode === "view" || (mode === "create" && isNextDisabled)}>
              Submit for Approval
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default StepBasicInfo;
